import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useCookies } from "react-cookie";
import {
  US,
  FR,
  DE,
  IT,
  ES,
  SA,
  CN,
  PT,
  RU,
  JP,
} from "country-flag-icons/react/3x2";
import {
  LANGUAGE_STORAGE_KEY,
  getCookieDomain,
  persistLanguageToStorage,
} from "../../utils/languageUtility";
import { useCardPreview } from "../../contexts/CardPreviewContext";

interface LanguageSelectorProps {
  className?: string;
  variant?: "compact" | "full";
  disabled?: boolean;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  className = "",
  variant = "compact",
  disabled: disabledProp = false,
}) => {
  const { t, i18n } = useTranslation("clientView");
  const isPreview = useCardPreview();
  const disabled = disabledProp || isPreview;
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    right: 0,
  });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [, setCookie] = useCookies([LANGUAGE_STORAGE_KEY]);

  const languages = [
    { code: "en", label: "English", flag: US },
    { code: "fr", label: "Français", flag: FR },
    { code: "de", label: "Deutsch", flag: DE },
    { code: "it", label: "Italiano", flag: IT },
    { code: "es", label: "Español", flag: ES },
    { code: "ar", label: "العربية", flag: SA },
    { code: "zh", label: "中文", flag: CN },
    { code: "pt", label: "Português", flag: PT },
    { code: "ru", label: "Русский", flag: RU },
    { code: "ja", label: "日本語", flag: JP },
  ];

  const currentLanguage =
    languages.find((lang) => i18n.language.startsWith(lang.code)) ||
    languages[0];

  const handleLanguageChange = (langCode: string) => {
    const persistLanguage = (language: string) => {
      if (typeof window === "undefined") return;

      persistLanguageToStorage(language, true);

      const domain = getCookieDomain();
      setCookie(LANGUAGE_STORAGE_KEY, language, {
        path: "/",
        maxAge: 60 * 60 * 24 * 365,
        sameSite: "none",
        secure: true,
        ...(domain ? { domain } : {}),
      });
    };

    persistLanguage(langCode);
    void i18n
      .changeLanguage(langCode)
      .then(() => persistLanguage(i18n.language));
    setIsOpen(false);
  };

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
  }, [isOpen]);

  if (variant === "compact") {
    const CurrentFlag = currentLanguage.flag;

    return (
      <div className={`relative ${className}`}>
        <button
          type="button"
          ref={buttonRef}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          tabIndex={disabled ? -1 : undefined}
          aria-hidden={disabled ? true : undefined}
          className={`flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200 overflow-hidden ${
            disabled
              ? "bg-gray-200 cursor-not-allowed opacity-50"
              : "bg-white/10 hover:bg-white/20 text-white hover:scale-105"
          }`}
          title={`${t("languageSelector.language")}: ${currentLanguage.label}`}
        >
          <CurrentFlag className="w-5 h-5 rounded-sm" />
        </button>

        {isOpen &&
          createPortal(
            <>
              <div
                className="fixed inset-0 z-[9998]"
                onClick={() => setIsOpen(false)}
              />
              <div
                className="fixed min-w-[140px] bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[9999]"
                style={{
                  top: `${dropdownPosition.top}px`,
                  right: `${dropdownPosition.right}px`,
                }}
              >
                {languages.map((lang) => {
                  const FlagComponent = lang.flag;
                  return (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      className={`w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors flex items-center gap-2 ${
                        currentLanguage.code === lang.code
                          ? "bg-blue-50 text-blue-600"
                          : "text-gray-700"
                      }`}
                    >
                      <FlagComponent className="w-5 h-4 rounded-sm" />
                      <span className="text-sm font-medium">{lang.label}</span>
                    </button>
                  );
                })}
              </div>
            </>,
            document.body
          )}
      </div>
    );
  }

  return (
    <div className={`w-full ${className}`}>
      <div className="text-white/70 text-sm font-medium mb-2">
        {t("languageSelector.language")}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {languages.map((lang) => {
          const FlagComponent = lang.flag;
          return (
            <button
              type="button"
              key={lang.code}
              onClick={
                disabled ? undefined : () => handleLanguageChange(lang.code)
              }
              disabled={disabled}
              tabIndex={disabled ? -1 : undefined}
              aria-hidden={disabled ? true : undefined}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                currentLanguage.code === lang.code
                  ? "bg-white/20 text-white border border-white/30"
                  : "bg-white/5 text-white/70 hover:bg-white/10 border border-white/10"
              }`}
            >
              <FlagComponent className="w-6 h-4 rounded-sm" />
              <span className="text-sm font-medium">{lang.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default LanguageSelector;
